import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { Funcionario } from '@/context/FuncionariosContext';
import { supabase } from '@/lib/supabase';
import { useFuncionarios } from '@/context/FuncionariosContext';
import { useAppSettings } from '@/context/AppSettingsContext';
import { calcularProgressao } from '@/utils/progressaoUtils';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import { useModalOverflow } from '@/hooks/useModalOverflow';

interface PontosManuaisModalProps {
  funcionario: Funcionario;
  pontosCalculados: number;
  onClose: () => void;
  onSuccess: () => void;
  isPrepav?: boolean;
}

type Operacao = 'adicionar' | 'remover' | 'definir';

const MOTIVOS_PRESET = [
  'Reconhecimento por desempenho excecional',
  'Correção de erro de lançamento',
  'Ajuste de transição de ciclo',
  'Bónus por projeto especial',
  'Desconto por ausência prolongada',
  'Ajuste manual por decisão de chefia',
];

const operacaoConfig = {
  adicionar: { label: 'Adicionar', icon: 'ri-add-circle-line',           activeBg: 'bg-emerald-50 border-emerald-300 text-emerald-700' },
  remover:   { label: 'Descontar', icon: 'ri-indeterminate-circle-line', activeBg: 'bg-rose-50 border-rose-300 text-rose-700'         },
  definir:   { label: 'Definir',   icon: 'ri-edit-circle-line',          activeBg: 'bg-amber-50 border-amber-300 text-amber-700'      },
};

export default function PontosManuaisModal({
  funcionario,
  pontosCalculados,
  onClose,
  onSuccess,
  isPrepav = false,
}: PontosManuaisModalProps) {
  const { reload } = useFuncionarios();
  const { settings } = useAppSettings();
  const { animating, handleClose } = useModalAnimation(onClose);
  useModalOverflow(true);

  const [operacao, setOperacao] = useState<Operacao>('adicionar');
  const [valor, setValor] = useState<string>('1');
  const [motivo, setMotivo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const valorNum = Math.max(0, parseFloat(valor) || 0);
  const pontosBase = funcionario.pontosAtuais ?? 0;
  const pontosDescontados = funcionario.pontosDescontados ?? 0;

  let novosPontosAtuais = pontosBase;
  let novosDescontados = pontosDescontados;
  if (operacao === 'adicionar') { novosPontosAtuais = pontosBase + valorNum; }
  else if (operacao === 'remover') { novosDescontados = pontosDescontados + valorNum; }
  else { novosPontosAtuais = valorNum; novosDescontados = 0; }

  const deltaManuais = novosPontosAtuais - pontosBase;
  const deltaDescontados = novosDescontados - pontosDescontados;
  const pontosTotaisResultado = Math.max(0, pontosCalculados + deltaManuais - deltaDescontados);

  const progAtual = calcularProgressao(pontosCalculados, funcionario.nivelAtual, settings.pontos_progressao);
  const progResultado = calcularProgressao(pontosTotaisResultado, funcionario.nivelAtual, settings.pontos_progressao);
  const ficaElegivel = progAtual.status !== 'pronto' && progResultado.status === 'pronto';
  const deixaElegivel = progAtual.status === 'pronto' && progResultado.status !== 'pronto';

  async function handleSave() {
    if (!motivo.trim()) { setError('Por favor indica o motivo do ajuste.'); return; }
    if (valorNum <= 0) { setError('O valor deve ser maior que zero.'); return; }
    setSaving(true); setError('');
    try {
      const updatePayload: Record<string, number> = {};
      if (operacao === 'adicionar') updatePayload.pontos_atuais = novosPontosAtuais;
      else if (operacao === 'remover') updatePayload.pontos_descontados = novosDescontados;
      else { updatePayload.pontos_atuais = novosPontosAtuais; updatePayload.pontos_descontados = 0; }

      const { error: updateErr } = await supabase.from('funcionarios').update(updatePayload).eq('id', funcionario.id);
      if (updateErr) throw new Error(updateErr.message);

      const descricaoOp =
        operacao === 'adicionar' ? `+${valorNum} pts manuais adicionados` :
        operacao === 'remover'   ? `-${valorNum} pts descontados` :
        `Pontos definidos para ${valorNum} pts (descontos zerados)`;

      await supabase.from('historico_alteracoes').insert({
        id: 'ha_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7),
        funcionario_id: funcionario.id,
        funcionario_nome: funcionario.nome,
        tipo_acao: 'edicao',
        campo_alterado: operacao === 'remover' ? 'pontos_descontados' : 'pontos_atuais',
        valor_anterior: String(operacao === 'remover' ? pontosDescontados : pontosBase),
        valor_novo: String(operacao === 'remover' ? novosDescontados : novosPontosAtuais),
        descricao: `Ajuste manual de pontos: ${descricaoOp}. Motivo: ${motivo}`,
        data_alteracao: new Date().toISOString(),
      });

      await reload();
      onSuccess();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erro ao guardar.');
    } finally {
      setSaving(false);
    }
  }

  const modal = (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-5xl max-h-[96vh] flex flex-col overflow-hidden border border-zinc-100 ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-5 border-b border-zinc-100 flex items-center justify-between flex-shrink-0 bg-zinc-50/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-amber-100 flex-shrink-0">
              <i className="ri-coins-line text-amber-600 text-base" />
            </div>
            <div>
              <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-0.5">Ajuste Manual</p>
              <h2 className="text-[15px] font-bold text-zinc-900 leading-tight truncate max-w-[200px]">{funcionario.nome}</h2>
            </div>
          </div>
          <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-5">

          {/* Aviso PREPAV */}
          {isPrepav && (
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                <i className="ri-star-fill text-amber-500 text-sm" />
              </div>
              <div>
                <p className="text-[12px] font-bold text-amber-800 mb-0.5">Exceção PREPAV ativa</p>
                <p className="text-[12px] text-amber-700 leading-relaxed">
                  Os pontos manuais são <strong>sempre somados</strong> aos pontos SIADAP.
                </p>
              </div>
            </div>
          )}

          {/* Estado atual */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2.5">Estado atual</p>
            <div className="grid grid-cols-3 gap-2">
              {[
                { label: 'Total atual',   value: pontosCalculados.toFixed(1), bg: 'bg-zinc-50 border-zinc-100',     val: 'text-zinc-900',   lbl: 'text-zinc-400'   },
                { label: 'Manuais (+)',   value: pontosBase.toFixed(1),       bg: 'bg-emerald-50 border-emerald-100',val: 'text-emerald-700',lbl: 'text-emerald-500'},
                { label: 'Descontos (−)', value: pontosDescontados.toFixed(1),bg: 'bg-rose-50 border-rose-100',     val: 'text-rose-600',   lbl: 'text-rose-400'   },
              ].map((s) => (
                <div key={s.label} className={`rounded-xl px-3 py-3 border text-center ${s.bg}`}>
                  <p className={`text-[11px] font-medium mb-1 ${s.lbl}`}>{s.label}</p>
                  <p className={`text-[20px] font-bold leading-tight ${s.val}`}>{s.value}</p>
                  <p className={`text-[11px] ${s.lbl}`}>pts</p>
                </div>
              ))}
            </div>
          </div>

          {/* Operação */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2.5">Operação</p>
            <div className="grid grid-cols-3 gap-2">
              {(Object.entries(operacaoConfig) as [Operacao, typeof operacaoConfig[Operacao]][]).map(([key, cfg]) => (
                <button
                  key={key}
                  onClick={() => setOperacao(key)}
                  className={`whitespace-nowrap flex flex-col items-center gap-1.5 px-2 py-3.5 rounded-xl border-2 text-[12px] font-semibold transition-all cursor-pointer ${
                    operacao === key ? cfg.activeBg : 'border-zinc-100 text-zinc-500 hover:border-zinc-200 hover:bg-zinc-50'
                  }`}
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${cfg.icon} text-base`} />
                  </div>
                  {cfg.label}
                </button>
              ))}
            </div>
          </div>

          {/* Valor */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2.5">
              {operacao === 'adicionar' ? 'Pontos a adicionar' : operacao === 'remover' ? 'Pontos a descontar' : 'Novo valor de pontos manuais'}
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => setValor((v) => String(Math.max(0, (parseFloat(v) || 0) - 0.5)))}
                className="whitespace-nowrap w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer text-lg font-bold flex-shrink-0">−</button>
              <input type="number" min={0} step={0.5} value={valor} onChange={(e) => setValor(e.target.value)}
                className="flex-1 px-4 py-2.5 text-center text-[20px] font-bold border border-zinc-200 rounded-lg focus:outline-none focus:ring-1 focus:ring-zinc-300 text-zinc-800" />
              <button onClick={() => setValor((v) => String((parseFloat(v) || 0) + 0.5))}
                className="whitespace-nowrap w-10 h-10 flex items-center justify-center rounded-lg border border-zinc-200 text-zinc-600 hover:bg-zinc-100 transition-colors cursor-pointer text-lg font-bold flex-shrink-0">+</button>
            </div>
            <div className="flex gap-1.5 mt-2.5 flex-wrap">
              {[0.5, 1, 1.5, 2, 3, 5].map((v) => (
                <button key={v} onClick={() => setValor(String(v))}
                  className={`whitespace-nowrap px-2.5 py-1 text-[12px] rounded-lg border transition-colors cursor-pointer ${
                    parseFloat(valor) === v ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-500 hover:border-zinc-400'
                  }`}>
                  {v} pts
                </button>
              ))}
            </div>
          </div>

          {/* Preview resultado */}
          <div className={`rounded-xl px-4 py-4 border-2 ${ficaElegivel ? 'bg-emerald-50 border-emerald-200' : deixaElegivel ? 'bg-rose-50 border-rose-200' : 'bg-zinc-50 border-zinc-100'}`}>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-3">Resultado após ajuste</p>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <span className="text-[13px] text-zinc-500">{pontosCalculados.toFixed(1)} pts</span>
                <div className="w-4 h-4 flex items-center justify-center text-zinc-400"><i className="ri-arrow-right-line text-sm" /></div>
                <span className={`text-[22px] font-bold ${ficaElegivel ? 'text-emerald-700' : deixaElegivel ? 'text-rose-600' : 'text-zinc-800'}`}>
                  {pontosTotaisResultado.toFixed(1)}<span className="text-[13px] font-normal ml-1 text-zinc-400">pts</span>
                </span>
              </div>
              <div>
                {ficaElegivel && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-700 bg-emerald-100 px-2.5 py-1 rounded-full"><i className="ri-checkbox-circle-fill text-sm" />Fica elegível!</span>}
                {deixaElegivel && <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 bg-rose-100 px-2.5 py-1 rounded-full"><i className="ri-close-circle-line text-sm" />Perde elegibilidade</span>}
                {!ficaElegivel && !deixaElegivel && <span className="text-[12px] text-zinc-400">{progResultado.status === 'pronto' ? 'Elegível' : `Faltam ${progResultado.pontosFaltam.toFixed(1)} pts`}</span>}
              </div>
            </div>
            <div className="h-1.5 bg-white/80 rounded-full overflow-hidden">
              <div className={`h-full rounded-full transition-all ${ficaElegivel ? 'bg-emerald-500' : deixaElegivel ? 'bg-rose-400' : 'bg-zinc-400'}`}
                style={{ width: `${Math.min(progResultado.percentagem, 100)}%` }} />
            </div>
            <p className="text-[11px] text-zinc-400 mt-1.5">{progResultado.percentagem.toFixed(0)}% do objetivo ({settings.pontos_progressao} pts)</p>
          </div>

          {/* Motivo */}
          <div>
            <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2.5">Motivo do ajuste *</p>
            <div className="flex flex-wrap gap-1.5 mb-2.5">
              {MOTIVOS_PRESET.map((m) => (
                <button key={m} onClick={() => { setMotivo(m); setError(''); }}
                  className={`whitespace-nowrap text-[12px] px-2.5 py-1 rounded-lg border transition-colors cursor-pointer ${
                    motivo === m ? 'bg-zinc-900 text-white border-zinc-900' : 'border-zinc-200 text-zinc-500 hover:border-zinc-400'
                  }`}>
                  {m}
                </button>
              ))}
            </div>
            <textarea value={motivo} onChange={(e) => { setMotivo(e.target.value); setError(''); }}
              rows={2} maxLength={300} placeholder="Descreve o motivo deste ajuste manual..."
              className="w-full px-3 py-2.5 text-[13px] border border-zinc-200 rounded-lg bg-white text-zinc-700 placeholder-zinc-400 focus:outline-none focus:ring-1 focus:ring-zinc-300 resize-none" />
            <div className="flex items-center justify-between mt-1">
              <span className="text-[11px] text-zinc-400">Campo obrigatório</span>
              <span className={`text-[11px] font-medium ${motivo.length > 270 ? 'text-amber-500' : 'text-zinc-400'}`}>{motivo.length}/300</span>
            </div>
          </div>

          {error && (
            <div className="flex items-center gap-2 text-[13px] text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5">
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0"><i className="ri-error-warning-line text-sm" /></div>
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-zinc-100 flex items-center justify-end gap-2.5 flex-shrink-0 bg-zinc-50/40">
          <button onClick={handleClose} disabled={saving}
            className="whitespace-nowrap px-4 py-2 text-[13px] text-zinc-600 border border-zinc-200 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer disabled:opacity-50">
            Cancelar
          </button>
          <button onClick={handleSave} disabled={saving || !motivo.trim()}
            className="whitespace-nowrap flex items-center gap-2 px-5 py-2 text-[13px] bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50">
            {saving
              ? <><i className="ri-loader-4-line animate-spin text-sm" />A guardar...</>
              : <><i className="ri-save-line text-sm" />Aplicar Ajuste</>
            }
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}